/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { FC, ReactElement, useState, ChangeEvent, useEffect, ReactNode } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import ChipInput from 'material-ui-chip-input'
import * as yup from 'yup'
import _ from 'lodash'
import { Form, Alert } from 'react-bootstrap'
import API from '@aws-amplify/api'
import { Formik, FieldArray } from 'formik'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import { observer, inject } from 'mobx-react'
import { MultiSelect } from 'react-multi-select-component'
import {
  InputLabel,
  FormControl,
  Select,
  Accordion,
  Tabs,
  Tab,
  Box,
  AppBar,
} from '@material-ui/core'

import { ToastTypeEnum } from '../Toasts'
import LoadingButton from '../../LoadingButton'
import { Settings as SettingsStore } from '../../../stores/settings'
import Spinner from '../../Spinner'
import {
  cityNamePrefill,
  headCountPrefill,
  industriesPrefill,
  locationPrefill,
  revenuesPrefill,
  seniorityLevelPrefill,
} from '../../../utils/constants'

interface TabPanelProps {
  children: ReactNode
  index: number
  value: number
}

const TabPanelType = (props: TabPanelProps): JSX.Element => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-type-${index}`}
      aria-labelledby={`scrollable-auto-tab-type-${index}`}
      {...other}
      className="tabpanel"
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  )
}

const a11yPropsType = (index: number): { id: string; 'aria-controls': string } => {
  return {
    id: `scrollable-auto-tab-type-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-type-${index}`,
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      width: '100%',
    },
    tabs: {
      '& .MuiTab-root': {
        flex: 1,
        minWidth: 'unset',
      },
      '& .MuiTabs-indicator': {
        backgroundColor: '#27a9e0',
      },
      '& .MuiTab-textColorPrimary.Mui-selected': {
        color: '#27a9e0',
      },
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: '23%',
      maxWidth: '23%',
      marginTop: theme.spacing(5),
      '@media (max-width: 1050px)': {
        minWidth: '30%',
        maxWidth: '30%',
      },
      '@media (max-width: 768px)': {
        minWidth: '45%',
        maxWidth: '45%',
      },
      '@media (max-width: 540px)': {
        minWidth: '100%',
        maxWidth: '100%',
        margin: 0,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
      },
    },
    noLabel: {
      marginTop: theme.spacing(3),
    },
  }),
)

const handleChangeMultiple = (
  event: ChangeEvent<{ value: unknown }>,
  setFieldValue: (string, any) => void,
  fieldName: string,
): void => {
  const { options } = event.target as HTMLSelectElement
  const value: string[] = []
  for (let i = 0, l = options.length; i < l; i += 1) {
    if (options[i].selected) {
      value.push(options[i].value)
    }
  }
  setFieldValue(fieldName, value)
}
const industryOptions = industriesPrefill?.map(industry => ({ label: industry, value: industry }))
const cityOptions = cityNamePrefill?.map(city => ({ label: city, value: city }))

interface Props extends RouteComponentProps {
  settings?: SettingsStore
}

const schema = yup.object({
  userCriteria: yup.array().of(
    yup.object({
      activated: yup.boolean(),
      companies: yup.array().when('companySearch', {
        is: true,
        then: yup.array().required('Companies are required for Company search'),
      }),
      companySearch: yup.boolean(),
      dncCompanies: yup.array().of(yup.string()),
      enabled: yup.boolean(),
      headCount: yup.array().when('enabled', {
        is: true,
        then: yup.array().required('Headcount is a required field'),
      }),
      industries: yup.array().when('enabled', {
        is: true,
        then: yup
          .array()
          .of(yup.string())
          .required('Industries is a required field'),
      }),
      cities: yup.array().when('enabled', {
        is: true,
        then: yup
          .array()
          .of(yup.string())
          .required('Cities is a required field'),
      }),
      locations: yup.array().when('enabled', {
        is: true,
        then: yup.array().required('Geographical Requirements is a required field'),
      }),
      revenues: yup.array().when('enabled', {
        is: true,
        then: yup
          .array()
          .of(yup.string())
          .required('Revenue is a required field'),
      }),
      missionName: yup.string(),
      seniorityLevels: yup.array().when('enabled', {
        is: true,
        then: yup.array().required('Seniority Levels is a required field'),
      }),
      titles: yup.array().of(yup.string()),
      titleExclusions: yup.array().of(yup.string()),
    }),
  ),
})
const ProspectCriteria: FC<Props> = ({ settings }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [currentValue, setValue] = useState(0)
  const classes = useStyles()

  const userCriteria = settings?.criteria?.map(criteria => ({
    ...criteria,
  }))
  const handleChange = (event: ChangeEvent<{}>, newValue: number): void => {
    setValue(newValue)
  }

  const updateCriteria = async (values): Promise<void> => {
    const localValues = values
    localValues.userCriteria.forEach((value, i) => {
      if (value.companySearch) {
        localValues.userCriteria[i].industries = ['all']
        localValues.userCriteria[i].cities = ['Any']
        localValues.userCriteria[i].dncCompanies = []
        localValues.userCriteria[i].headCount = ['All']
        localValues.userCriteria[i].locations = ['Any']
      } else {
        localValues.userCriteria[i].companies = []
      }
    })
    setIsLoading(true)

    let myInit = {}
    console.log(',,,,,,', localValues.userCriteria)
    myInit = {
      body: { criteria: localValues.userCriteria },
    }

    try {
      await API.post('mav-outlook-api', '/V2/users/prospectCriteria', myInit)

      settings?.pushToToasts({
        type: ToastTypeEnum.INFO,
        message:
          "Your changes are being processed. It will take some time before they're available on this page.",
      })
      settings?.getCriteria()
      setIsLoading(false)
    } catch (e) {
      console.error(e)

      setIsLoading(false)
    }
  }

  useEffect(() => {
    settings?.getCriteria()
  }, [])
  if (settings?.isLoading) {
    return <Spinner />
  }

  return (
    <div className="criteria p-4">
      <div className="w-100">
        <Formik
          validationSchema={schema}
          onSubmit={updateCriteria}
          initialValues={{
            userCriteria,
          }}
          enableReinitialize
        >
          {({
            handleSubmit,
            values,
            touched,
            errors,
            submitCount,
            setFieldValue,
          }): ReactElement => {
            return (
              <Form noValidate onSubmit={handleSubmit}>
                <Accordion>
                  <FieldArray name="criteria">
                    {(): ReactElement => {
                      return (
                        <div className={classes.root}>
                          <AppBar position="static" color="default">
                            <Tabs
                              variant="scrollable"
                              textColor="primary"
                              value={currentValue}
                              onChange={handleChange}
                              aria-label="scrollable auto tabs"
                              className={classes.tabs}
                            >
                              {settings?.criteria?.map((_criteria, i) => (
                                <Tab
                                  label={`Recon ${i + 1}`}
                                  key={`Recon ${i + 1}`}
                                  {...a11yPropsType(i)}
                                />
                              ))}
                            </Tabs>
                          </AppBar>
                          {settings?.criteria?.map((_criteria, i) => {
                            const industries = {
                              value: _.get(values, `userCriteria.${i}.industries`) || [],
                              touched: _.get(touched, `userCriteria.${i}.industries`),
                              errors: _.get(errors, `userCriteria.${i}.industries`),
                            }
                            const titles = {
                              value: _.get(values, `userCriteria.${i}.titles`),
                              touched: _.get(touched, `userCriteria.${i}.titles`),
                              errors: _.get(errors, `userCriteria.${i}.titles`),
                            }
                            const titleExclusions = {
                              value: _.get(values, `userCriteria.${i}.titleExclusions`),
                              touched: _.get(touched, `userCriteria.${i}.titleExclusions`),
                              errors: _.get(errors, `userCriteria.${i}.titleExclusions`),
                            }
                            const dncCompanies = {
                              value: _.get(values, `userCriteria.${i}.dncCompanies`),
                              touched: _.get(touched, `userCriteria.${i}.dncCompanies`),
                              errors: _.get(errors, `userCriteria.${i}.dncCompanies`),
                            }
                            const headCount = {
                              value: _.get(values, `userCriteria.${i}.headCount`),
                              touched: _.get(touched, `userCriteria.${i}.headCount`),
                              errors: _.get(errors, `userCriteria.${i}.headCount`),
                            }
                            const seniorityLevels = {
                              value: _.get(values, `userCriteria.${i}.seniorityLevels`),
                              touched: _.get(touched, `userCriteria.${i}.seniorityLevels`),
                              errors: _.get(errors, `userCriteria.${i}.seniorityLevels`),
                            }
                            const locations = {
                              value: _.get(values, `userCriteria.${i}.locations`),
                              touched: _.get(touched, `userCriteria.${i}.locations`),
                              errors: _.get(errors, `userCriteria.${i}.locations`),
                            }
                            const revenues = {
                              value: _.get(values, `userCriteria.${i}.revenues`),
                              touched: _.get(touched, `userCriteria.${i}.revenues`),
                              errors: _.get(errors, `userCriteria.${i}.revenues`),
                            }
                            const companies = {
                              value: _.get(values, `userCriteria.${i}.companies`),
                              touched: _.get(touched, `userCriteria.${i}.companies`),
                              errors: _.get(errors, `userCriteria.${i}.companies`),
                            }
                            const enabled = {
                              value: _.get(values, `userCriteria.${i}.enabled`),
                              touched: _.get(touched, `userCriteria.${i}.enabled`),
                              errors: _.get(errors, `userCriteria.${i}.enabled`),
                            }
                            const activated = {
                              value: _.get(values, `userCriteria.${i}.activated`),
                              touched: _.get(touched, `userCriteria.${i}.activated`),
                              errors: _.get(errors, `userCriteria.${i}.activated`),
                            }
                            const missionName = {
                              value: _.get(values, `userCriteria.${i}.missionName`),
                              touched: _.get(touched, `userCriteria.${i}.missionName`),
                              errors: _.get(errors, `userCriteria.${i}.missionName`),
                            }
                            const cities = {
                              value: _.get(values, `userCriteria.${i}.cities`),
                              touched: _.get(touched, `userCriteria.${i}.cities`),
                              errors: _.get(errors, `userCriteria.${i}.cities`),
                            }
                            if (!missionName.value) {
                              setFieldValue(
                                `userCriteria.${i}.missionName`,
                                userCriteria &&
                                  userCriteria[i] &&
                                  userCriteria[i].missions &&
                                  userCriteria[i].missions.firstTouch
                                  ? userCriteria[i].missions.firstTouch[0]
                                  : 'Mission 1',
                              )
                            }
                            const companySearch = {
                              value: _.get(values, `userCriteria.${i}.companySearch`),
                              touched: _.get(touched, `userCriteria.${i}.companySearch`),
                              errors: _.get(errors, `userCriteria.${i}.companySearch`),
                            }
                            return (
                              <TabPanelType
                                value={currentValue}
                                index={i}
                                key={`${'maneuverType'}-touch-${i + 1}`}
                              >
                                <div style={{ maxWidth: '100%', width: '100%' }}>
                                  <Form.Group controlId="companySearch" className="companySearch">
                                    <Form.Check
                                      label="Company Recon"
                                      // className="switch"
                                      id={`userCriteria.${i}.companySearch`}
                                      type="switch"
                                      checked={companySearch.value}
                                      onChange={(): void => {
                                        setFieldValue(
                                          `userCriteria.${i}.companySearch`,
                                          !companySearch.value,
                                        )
                                      }}
                                    />
                                  </Form.Group>
                                  <FormControl
                                    className={classes.formControl}
                                    style={!companySearch.value ? {} : { display: 'none' }}
                                  >
                                    <InputLabel shrink htmlFor="headCounts-multiselect">
                                      Head Counts
                                    </InputLabel>
                                    <Select
                                      disabled={companySearch.value}
                                      multiple
                                      native
                                      value={headCount.value}
                                      onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                        handleChangeMultiple(
                                          event,
                                          setFieldValue,
                                          `userCriteria.${i}.headCount`,
                                        )
                                      }}
                                      inputProps={{
                                        id: 'headCounts-multiselect',
                                      }}
                                    >
                                      {headCountPrefill?.map(name => (
                                        <option key={name} value={name}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl className={classes.formControl}>
                                    <InputLabel shrink htmlFor="seniorityLevels-multiselect">
                                      Seniority Levels
                                    </InputLabel>
                                    <Select
                                      multiple
                                      native
                                      value={seniorityLevels.value}
                                      onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                        handleChangeMultiple(
                                          event,
                                          setFieldValue,
                                          `userCriteria.${i}.seniorityLevels`,
                                        )
                                      }}
                                      inputProps={{
                                        id: 'seniorityLevels-multiselect',
                                      }}
                                    >
                                      {seniorityLevelPrefill.map(name => (
                                        <option key={name} value={name}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl
                                    className={classes.formControl}
                                    style={!companySearch.value ? {} : { display: 'none' }}
                                  >
                                    <InputLabel shrink htmlFor="location-multiselect">
                                      Geographical Requirements
                                    </InputLabel>
                                    <Select
                                      disabled={companySearch.value}
                                      multiple
                                      native
                                      value={locations.value}
                                      onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                        handleChangeMultiple(
                                          event,
                                          setFieldValue,
                                          `userCriteria.${i}.locations`,
                                        )
                                      }}
                                      inputProps={{
                                        id: 'location-multiselect',
                                      }}
                                    >
                                      {locationPrefill.map(name => (
                                        <option key={name} value={name}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl
                                    className={classes.formControl}
                                    style={!companySearch.value ? {} : { display: 'none' }}
                                  >
                                    <InputLabel shrink htmlFor="revenues-multiselect">
                                      Revenue
                                    </InputLabel>
                                    <Select
                                      disabled={companySearch.value}
                                      multiple
                                      native
                                      value={revenues.value}
                                      onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                        handleChangeMultiple(
                                          event,
                                          setFieldValue,
                                          `userCriteria.${i}.revenues`,
                                        )
                                      }}
                                      inputProps={{
                                        id: 'revenues-multiselect',
                                      }}
                                    >
                                      {revenuesPrefill.map(name => (
                                        <option key={name} value={name}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {/* <FormControl
                                    className={classes.formControl}
                                    style={!companySearch.value ? {} : { display: 'none' }}
                                  >
                                    <InputLabel shrink htmlFor="city-multiselect">
                                      City Requirements
                                    </InputLabel>
                                    {console.log(`sdfg`,JSON.stringify(locations.value), locations.value.length, locations.value[0],locations.value[0] === 'Any'  )}
                                    <Select
                                      disabled={locations.value[0] === 'Any' }
                                      multiple
                                      native
                                      value={cities.value}
                                      onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                        handleChangeMultiple(
                                          event,
                                          setFieldValue,
                                          `userCriteria.${i}.cities`,
                                        )
                                      }}
                                      inputProps={{
                                        id: 'cities-multiselect',
                                      }}
                                    >
                                      {cityNamePrefill.map(name => (
                                        <option key={name} value={name}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl> */}
                                  <Form.Group
                                    controlId={`userCriteria-${i}-companies`}
                                    className="criteria-input"
                                    style={
                                      companySearch.value
                                        ? { display: 'block' }
                                        : { display: 'none' }
                                    }
                                  >
                                    <ChipInput
                                      disabled={!companySearch.value}
                                      id="companies"
                                      label="Companies"
                                      helperText="Example: Maverick, Google, Microsoft, etc"
                                      value={companies.value}
                                      onAdd={(chip): void => {
                                        setFieldValue(`userCriteria.${i}.companies`, [
                                          ...companies.value,
                                          chip,
                                        ])
                                      }}
                                      onDelete={(chip, index): void => {
                                        const tmpArr = [...companies.value]
                                        tmpArr.splice(index, 1)
                                        setFieldValue(`userCriteria.${i}.companies`, tmpArr)
                                      }}
                                    />
                                    {companies.errors && companies.touched ? (
                                      <Alert variant="danger">{companies.errors}</Alert>
                                    ) : null}
                                  </Form.Group>
                                  <Form.Group
                                    controlId={`userCriteria.${i}.cities`}
                                    className="criteria-input"
                                    style={
                                      !locations.value.includes('Any')
                                        ? { display: 'block' }
                                        : { display: 'none', maxWidth: '800px' }
                                    }
                                  >
                                    <InputLabel shrink htmlFor={`userCriteria.${i}.cities`}>
                                      Cities
                                    </InputLabel>
                                    {/* eslint-disable prettier/prettier, @typescript-eslint/indent */}
                                    <MultiSelect
                                      overrideStrings={{ selectSomeItems: 'Cities...' }}
                                      disabled={locations.value[0] === 'Any'}
                                      options={cityOptions}
                                      value={
                                        cities.value &&
                                        !companySearch.value &&
                                        locations.value[0] !== 'Any'
                                          ? cities.value.map(city => ({
                                              label: city,
                                              value: city,
                                            }))
                                          : cityOptions
                                      }
                                      onChange={(value): void => {
                                        setFieldValue(
                                          `userCriteria.${i}.cities`,
                                          value.map(function makeArr(obj) {
                                            return obj.value
                                          }),
                                        )
                                      }}
                                      labelledBy={`userCriteria.${i}.cities`}
                                    />
                                    {/* eslint-enable prettier/prettier, @typescript-eslint/indent */}
                                    {cities.errors && cities.touched ? (
                                      <Alert variant="danger">{cities.errors}</Alert>
                                    ) : null}
                                  </Form.Group>
                                  <Form.Group
                                    controlId={`userCriteria.${i}.industries`}
                                    className="criteria-input"
                                    style={
                                      !companySearch.value
                                        ? { display: 'block' }
                                        : { display: 'none', maxWidth: '800px' }
                                    }
                                  >
                                    <InputLabel shrink htmlFor={`userCriteria.${i}.industries`}>
                                      Industries
                                    </InputLabel>
                                    {/* eslint-disable prettier/prettier, @typescript-eslint/indent */}
                                    <MultiSelect
                                      overrideStrings={{ selectSomeItems: 'Industries...' }}
                                      disabled={companySearch.value}
                                      options={industryOptions}
                                      value={
                                        industries.value &&
                                        !companySearch.value &&
                                        industries.value[0] !== 'all'
                                          ? industries.value.map(industry => ({
                                              label: industry,
                                              value: industry,
                                            }))
                                          : industryOptions
                                      }
                                      onChange={(value): void => {
                                        setFieldValue(
                                          `userCriteria.${i}.industries`,
                                          value.map(function makeArr(obj) {
                                            return obj.value
                                          }),
                                        )
                                      }}
                                      labelledBy={`userCriteria.${i}.industries`}
                                    />
                                    {/* eslint-enable prettier/prettier, @typescript-eslint/indent */}
                                    {industries.errors && industries.touched ? (
                                      <Alert variant="danger">{industries.errors}</Alert>
                                    ) : null}
                                  </Form.Group>
                                  <Form.Group
                                    controlId={`userCriteria-${i}-titles`}
                                    className="criteria-input"
                                  >
                                    <ChipInput
                                      label="Title Keywords"
                                      helperText="Example: Sales, Growth, Technology, Security, etc."
                                      value={titles.value}
                                      onAdd={(chip): void => {
                                        setFieldValue(`userCriteria.${i}.titles`, [
                                          ...titles.value,
                                          chip,
                                        ])
                                      }}
                                      onDelete={(chip, index): void => {
                                        const tmpArr = [...titles.value]
                                        tmpArr.splice(index, 1)
                                        setFieldValue(`userCriteria.${i}.titles`, tmpArr)
                                      }}
                                      fullWidth
                                    />
                                  </Form.Group>
                                  <Form.Group
                                    controlId={`userCriteria-${i}-titleExclusions`}
                                    className="criteria-input"
                                  >
                                    <ChipInput
                                      label="Exclude Title Keywords"
                                      helperText="Example: Sales, Growth, Technology, Security, etc."
                                      value={titleExclusions.value}
                                      onAdd={(chip): void => {
                                        setFieldValue(`userCriteria.${i}.titleExclusions`, [
                                          ...titleExclusions.value,
                                          chip,
                                        ])
                                      }}
                                      onDelete={(chip, index): void => {
                                        const tmpArr = [...titleExclusions.value]
                                        tmpArr.splice(index, 1)
                                        setFieldValue(`userCriteria.${i}.titleExclusions`, tmpArr)
                                      }}
                                      fullWidth
                                    />
                                  </Form.Group>
                                  <Form.Group
                                    controlId="dnc"
                                    className="criteria-input"
                                    style={
                                      !companySearch.value
                                        ? { display: 'block' }
                                        : { display: 'none' }
                                    }
                                  >
                                    <ChipInput
                                      disabled={companySearch.value}
                                      label="Do Not Research Domains"
                                      helperText="Example: microsoft.com, gmail.com, maverickapp.io"
                                      value={dncCompanies.value}
                                      onAdd={(chip): void => {
                                        setFieldValue(`userCriteria.${i}.dncCompanies`, [
                                          ...dncCompanies.value,
                                          chip,
                                        ])
                                      }}
                                      onDelete={(chip, index): void => {
                                        const tmpArr = [...dncCompanies.value]
                                        tmpArr.splice(index, 1)
                                        setFieldValue(`userCriteria.${i}.dncCompanies`, tmpArr)
                                      }}
                                      fullWidth
                                    />
                                  </Form.Group>
                                  <Form.Group controlId="enabled" className="enabled">
                                    <Form.Check
                                      label="Search Enabled"
                                      isInvalid={submitCount > 0 && !!enabled.errors}
                                      checked={enabled.value}
                                      onChange={(): void => {
                                        setFieldValue(`userCriteria.${i}.enabled`, !enabled.value)
                                        if (!enabled.value) {
                                          setFieldValue(`userCriteria.${i}.activated`, false)
                                        }
                                      }}
                                    />
                                  </Form.Group>
                                  <Form.Group
                                    controlId="activated"
                                    className="activated"
                                    style={
                                      enabled.value ? { display: 'block' } : { display: 'none' }
                                    }
                                  >
                                    <Form.Check
                                      disabled={!enabled.value}
                                      label="Auto Schedule Matched Prospects"
                                      isInvalid={submitCount > 0 && !!activated.errors}
                                      checked={activated.value}
                                      onChange={(): void => {
                                        setFieldValue(
                                          `userCriteria.${i}.activated`,
                                          !activated.value,
                                        )
                                      }}
                                    />
                                  </Form.Group>
                                  <Form.Group
                                    controlId="campaign"
                                    style={
                                      activated.value && enabled.value
                                        ? { display: 'block' }
                                        : { display: 'none' }
                                    }
                                  >
                                    <Form.Label>Selected Mission:</Form.Label>
                                    <Form.Control
                                      as="select"
                                      disabled={!activated.value}
                                      value={missionName.value}
                                      onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                                        setFieldValue(
                                          `userCriteria.${i}.missionName`,
                                          e.currentTarget.value,
                                        )
                                      }}
                                    >
                                      {userCriteria
                                        ? userCriteria[i]?.missions.firstTouch.map(
                                            currentMissionName => (
                                              <option
                                                key={currentMissionName}
                                                value={currentMissionName}
                                              >
                                                {currentMissionName}
                                              </option>
                                            ),
                                          )
                                        : ''}
                                    </Form.Control>
                                  </Form.Group>
                                  <Form.Group
                                    controlId={`actions-${i}`}
                                    className="save-template-group"
                                  >
                                    <LoadingButton
                                      variant="primary"
                                      isLoading={isLoading}
                                      type="submit"
                                      block
                                    >
                                      Update Criteria
                                    </LoadingButton>
                                  </Form.Group>
                                </div>
                              </TabPanelType>
                            )
                          })}
                        </div>
                      )
                    }}
                  </FieldArray>
                </Accordion>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}
export default inject('settings')(observer(ProspectCriteria))
